import { useMemo, useCallback } from 'react';
import { useFilterState } from './use-filter-state';
import type { ChipDerivation, FilterChip, UseFilterStateReturn } from './use-filter-state';
import { useSessionStorage } from './use-session-storage';
import { useDebouncedValue } from './use-debounce';
import {
  DEFAULT_DRAWER_FILTERS,
  SESSION_KEY_SEARCH,
  SESSION_KEY_HIDE_INACTIVE,
  SESSION_KEY_DRAWER,
} from '@/constants/course-catalog-constants';
import type { CourseCatalogDrawerFilters } from '@/constants/course-catalog-constants';
import type { CourseFilters } from '@/types';
import { CourseStatus } from '@/types';

function sortByLabel(a: { labelText: string }, b: { labelText: string }): number {
  return a.labelText.localeCompare(b.labelText);
}

const chipDerivation: ChipDerivation<CourseCatalogDrawerFilters> = {
  deriveChips(state) {
    const chips: FilterChip[] = [];

    for (const schoolName of state.schools) {
      chips.push({ id: `school:${schoolName}`, dataText: `School: ${schoolName}` });
    }
    for (const sa of state.subjectAreas) {
      chips.push({ id: `subjectArea:${sa}`, dataText: `Subject area: ${sa}` });
    }
    for (const g of state.grades) {
      chips.push({ id: `grade:${g}`, dataText: `Grade: ${g}` });
    }

    return chips;
  },

  removeChip(state, chipId) {
    const colonIdx = chipId.indexOf(':');
    const category = chipId.slice(0, colonIdx);
    const value = chipId.slice(colonIdx + 1);
    switch (category) {
      case 'school':
        return { ...state, schools: state.schools.filter((s) => s !== value) };
      case 'subjectArea':
        return { ...state, subjectAreas: state.subjectAreas.filter((s) => s !== value) };
      case 'grade':
        return { ...state, grades: state.grades.filter((g) => g !== value) };
      default:
        return state;
    }
  },
};

interface SchoolOption {
  id: string;
  name: string;
  labelText: string;
}
interface SubjectAreaOption {
  id: string;
  name: string;
  labelText: string;
}

export interface UseCourseCatalogFiltersOptions {
  schoolOptions?: SchoolOption[];
  subjectAreaOptions?: SubjectAreaOption[];
  isSchoolLocked?: boolean;
}

export interface UseCourseCatalogFiltersReturn {
  searchTerm: string;
  setSearchTerm: (value: string | ((prev: string) => string)) => void;
  hideInactive: boolean;
  setHideInactive: (value: boolean | ((prev: boolean) => boolean)) => void;
  drawerState: UseFilterStateReturn<CourseCatalogDrawerFilters>;
  handleApply: () => void;
  handleClearAll: () => void;
  handleChipRemove: (chipId: string) => void;
  sortedSchoolOptions: SchoolOption[];
  sortedSubjectAreaOptions: SubjectAreaOption[];
  isSchoolLocked: boolean;
  queryFilters: CourseFilters;
}

export function useCourseCatalogFilters(
  options: UseCourseCatalogFiltersOptions = {}
): UseCourseCatalogFiltersReturn {
  const { schoolOptions = [], subjectAreaOptions = [], isSchoolLocked = false } = options;

  const [searchTerm, setSearchTerm] = useSessionStorage(SESSION_KEY_SEARCH, '');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const [hideInactive, setHideInactive] = useSessionStorage(SESSION_KEY_HIDE_INACTIVE, true);
  const [savedDrawerFilters, setSavedDrawerFilters] = useSessionStorage(
    SESSION_KEY_DRAWER,
    DEFAULT_DRAWER_FILTERS
  );

  const filterState = useFilterState<CourseCatalogDrawerFilters>(
    DEFAULT_DRAWER_FILTERS,
    chipDerivation,
    savedDrawerFilters
  );

  const handleApply = useCallback(() => {
    filterState.applyFilters();
    setSavedDrawerFilters(filterState.pendingFilters);
  }, [filterState, setSavedDrawerFilters]);

  const handleClearAll = useCallback(() => {
    filterState.resetFilters();
    setSavedDrawerFilters(DEFAULT_DRAWER_FILTERS);
  }, [filterState, setSavedDrawerFilters]);

  const handleChipRemove = useCallback(
    (chipId: string) => {
      filterState.removeChip(chipId);
      setSavedDrawerFilters((prev) => chipDerivation.removeChip(prev, chipId));
    },
    [filterState, setSavedDrawerFilters]
  );

  const sortedSchoolOptions = useMemo(() => [...schoolOptions].sort(sortByLabel), [schoolOptions]);

  const sortedSubjectAreaOptions = useMemo(
    () => [...subjectAreaOptions].sort(sortByLabel),
    [subjectAreaOptions]
  );

  const appliedFilters = filterState.appliedFilters;
  const queryFilters: CourseFilters = useMemo(
    () => ({
      search: debouncedSearchTerm || undefined,
      status: hideInactive ? CourseStatus.ACTIVE : undefined,
      schools: appliedFilters.schools.length > 0 ? appliedFilters.schools.join(',') : undefined,
      subjectAreas:
        appliedFilters.subjectAreas.length > 0 ? appliedFilters.subjectAreas : undefined,
      grades: appliedFilters.grades.length > 0 ? appliedFilters.grades.join(',') : undefined,
    }),
    [debouncedSearchTerm, hideInactive, appliedFilters]
  );

  return {
    searchTerm,
    setSearchTerm,
    hideInactive,
    setHideInactive,

    drawerState: filterState,
    handleApply,
    handleClearAll,
    handleChipRemove,

    sortedSchoolOptions,
    sortedSubjectAreaOptions,
    isSchoolLocked,

    queryFilters,
  };
}
